import DateTimeHelpers from "helpers/DateTimeHelpers";
import PurchaseService from "services/PurchaseService";
import { db } from "helpers/DBHelper";
import { COLLECTIONS, DRAFT_STATUS, CASHBOOK_ENTRY_TYPE } from "const";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";
import * as ShiftHelper from "helpers/ShiftHelper";
import VendorHelper from "helpers/VendorHelper";

export function getPurchaseItemTax(taxPercentage, amount) {
  const taxPercent = Number(taxPercentage) || 0;
  const amt = Number(amount) || 0;
  return NumberFormatHelper.amountExtendedRoundOff((amt * taxPercent) / 100);
}

export function calculatePurchaseItem(data) {
  const { id, isExisting, item, quantity, rate, discount, taxPercentage, manualTaxAmount, cost, profit_margin } = data;
  const qty = Number(quantity) || 0;
  const unitPrice = NumberFormatHelper.amountRoundOff(Number(rate) || 0);
  const itemCost = Number(cost) || 0;
  const disc = Number(discount) || 0;
  const taxPercent = Number(taxPercentage) || 0;
  const pMargin = Number(profit_margin || 0);

  let tax = 0;
  let finalTaxPercentage = taxPercent;
  const baseCost = itemCost * qty;

  const hasManualTax = manualTaxAmount != null && (Number(manualTaxAmount) > 0 || taxPercent === 0);

  if (hasManualTax) {
    tax = Number(manualTaxAmount);
    if (baseCost !== 0) {
      finalTaxPercentage = (tax * 100) / baseCost;
    }
  } else {
    tax = getPurchaseItemTax(taxPercent, baseCost);
  }

  const total = baseCost - disc + tax;

  return {
    ...(id ? { id } : {}),
    isExisting: isExisting,
    item: item,
    item_id: item?.id,
    plu: item?.plu,
    name: item?.name,
    rate,
    price: unitPrice,
    cost: itemCost,
    profit_margin: pMargin,
    quantity: qty,
    discount: disc,
    taxPercentage: finalTaxPercentage,
    tax: tax,
    total: total,
    stock: (item?.stock || 0) - qty,
    payable: total,
  };
}

export function calculateTotals(items) {
  return (items || []).reduce(
    (acc, item) => {
      acc.qty += Number(item.quantity);
      acc.tax += Number(item.tax);
      acc.total += Number(item.total);
      acc.discount += Number(item.discount || 0);
      return acc;
    },
    { qty: 0, tax: 0, total: 0, discount: 0 },
  );
}

export async function fetchTodayPurchases() {
  const currentShiftId = ShiftHelper.getCurrentShiftId()?.id || null;

  const savedPurchases = await db.filter(
    COLLECTIONS.PURCHASES.name,
    (p) => p.status === DRAFT_STATUS.SAVED && p?.shift?.id === currentShiftId,
  );

  return savedPurchases
    .map((p) => {
      const itemsList = p.items || [];
      const totalPurchaseAmount = itemsList.reduce((acc, item) => acc + Number(item?.total || item?.payable || 0), 0);

      return {
        ...p,
        id: p.id,
        local_id: p.local_id,
        invoiceNo: p.invoiceNo,
        date: p.date,
        billDate: p.billDate,
        vendor: p.vendor,
        totalPurchaseAmount,
        paidAmount: p.paidAmount,
        items: itemsList,
      };
    })
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
}

export async function fetchDraftPurchases() {
  const drafts = await db.filter(COLLECTIONS.PURCHASES.name, (d) => d.status === DRAFT_STATUS.DRAFT);
  return drafts.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
}

export async function getLocalPurchase(id) {
  if (!id) return null;
  const localMatches = await db.filter(COLLECTIONS.PURCHASES.name, (p) => String(p.id) === String(id));
  return localMatches?.[0] || null;
}

export async function getDraftPurchase(localId) {
  return await db.get(COLLECTIONS.PURCHASES.name, Number(localId));
}

function buildPurchaseItemPayload(purchaseItem, includeId = false) {
  const itemPayload = {
    item: {
      ...(purchaseItem.item || {}),
      price: NumberFormatHelper.amountRoundOff(Number(purchaseItem.rate || 0)),
      config: {
        ...((purchaseItem.item && purchaseItem.item.config) || {}),
        profit_margin: NumberFormatHelper.amountRoundOff(Number(purchaseItem.profit_margin || 0)),
      },
    },
    item_id: purchaseItem.item?.id,
    payable: NumberFormatHelper.amountRoundOff(Number(purchaseItem.payable || purchaseItem.total || 0)),
    plu: purchaseItem.plu,
    price: NumberFormatHelper.amountRoundOff(Number(purchaseItem.rate || 0)),
    profit_margin: NumberFormatHelper.amountRoundOff(Number(purchaseItem.profit_margin || 0)),
    quantity: NumberFormatHelper.amountRoundOff(Number(purchaseItem.quantity || 0)),
    rate: NumberFormatHelper.amountRoundOff(Number(purchaseItem.cost || 0)),
    stock: NumberFormatHelper.amountRoundOff(Number(purchaseItem.stock || 0)),
    tax: NumberFormatHelper.amountRoundOff(Number(purchaseItem.tax || 0)),
    taxPercentage: NumberFormatHelper.amountRoundOff(Number(purchaseItem.taxPercentage || 0)),
  };

  if (includeId) {
    if (purchaseItem.id) {
      itemPayload.id = purchaseItem.id;
    }
  } else {
    itemPayload.new_item = true;
  }

  return itemPayload;
}

export async function createPurchaseShiftPayments(data, purchaseId) {
  const currentShiftId = ShiftHelper.getCurrentShiftId()?.id;
  if (!currentShiftId) return;

  const { invoiceNo = "", vendor, purchaseDate, paymentModes = [] } = data;

  if (purchaseId) {
    const existing = await ShiftHelper.filterShiftPayments({ shift_id: Number(currentShiftId) });
    const txns = existing.filter(
      (sp) => sp.type === CASHBOOK_ENTRY_TYPE.PURCHASE && String(sp.purchase_id) === String(purchaseId),
    );
    for (const t of txns) await db.delete(COLLECTIONS.SHIFT_TRANSACTIONS.name, t.id);
  }

  const vendorName = vendor?.name || "";
  const description = `${CASHBOOK_ENTRY_TYPE.PURCHASE}: Invoice #${invoiceNo}${vendorName ? " - " + vendorName : ""}`;
  const base = {
    shift_id: currentShiftId,
    amount: 0,
    description,
    type: CASHBOOK_ENTRY_TYPE.PURCHASE,
    time: DateTimeHelpers.getLocalDateTimeAsUTCString(purchaseDate),
    purchase_id: purchaseId,
    purchase_local_id: data?.local_id || null,
    invoice_no: invoiceNo,
    vendor_name: vendorName,
  };

  for (const pm of paymentModes) {
    const amt = Number(pm.amount) || 0;
    if (amt > 0) {
      await ShiftHelper.addShiftPayment({ ...base, local_id: Number(pm.mode_id || pm.id), name: pm.name, debit: amt });
    }
  }
}

export async function savePurchase(data) {
  const { invoiceNo, vendor, purchaseDate, billDate, paymentDate, purchaseItems, paidAmount, paymentModes = [] } = data;

  const payload = {
    bill_date: DateTimeHelpers.formatISODateToDMY(billDate),
    date: DateTimeHelpers.getLocalDateTimeAsUTCString(purchaseDate),
    invoice_no: invoiceNo,
    items: (purchaseItems || []).map((pi) => buildPurchaseItemPayload(pi)),
    paid_amount: NumberFormatHelper.amountRoundOff(Number(paidAmount)),
    payment_date: DateTimeHelpers.formatISODateToDMY(paymentDate),
    payment_modes: (paymentModes || []).map((pm) => ({
      mode_id: Number(pm.mode_id || pm.id),
      name: pm.name,
      amount: NumberFormatHelper.amountRoundOff(Number(pm.amount) || 0),
    })),
    vendor_id: vendor?.id,
    vendor_name_old: "",
  };

  const response = await PurchaseService.addPurchase(payload);
  await createPurchaseShiftPayments(data, response?.id);
  const vendorId = vendor?.id;
  if (vendorId && Number(vendorId) > 0) {
    VendorHelper.fetchAndUpdateVendorFromBE(vendorId);
  }
  return response;
}

export async function updatePurchase(id, data) {
  const {
    invoiceNo,
    vendor,
    purchaseDate,
    billDate,
    paymentDate,
    items,
    newItems,
    removeItems,
    totalAmount,
    paidAmount,
    paymentModes = [],
  } = data;

  const payload = {
    id: Number(id),
    bill_date: DateTimeHelpers.formatISODateToDMY(billDate),
    date: DateTimeHelpers.getLocalDateTimeAsUTCString(purchaseDate),
    invoice_no: invoiceNo,
    items: items.map((pi) => buildPurchaseItemPayload(pi, true)),
    new_items: newItems.map((pi) => buildPurchaseItemPayload(pi)),
    remove_items: removeItems,
    paid_amount: NumberFormatHelper.amountRoundOff(Number(paidAmount)),
    payment_date: DateTimeHelpers.formatISODateToDMY(paymentDate),
    payment_modes: (paymentModes || []).map((pm) => ({
      mode_id: Number(pm.mode_id),
      name: pm.name,
      amount: NumberFormatHelper.amountRoundOff(Number(pm.amount) || 0),
    })),
    total_amount: NumberFormatHelper.amountRoundOff(Number(totalAmount)),
    vendor_id: vendor?.id,
    vendor_name_old: "",
  };

  const response = await PurchaseService.updatePurchase(payload, id);
  await createPurchaseShiftPayments({ ...data, id });
  const vendorId = vendor?.id;
  if (vendorId && Number(vendorId) > 0) {
    VendorHelper.fetchAndUpdateVendorFromBE(vendorId);
  }
  return response;
}

export async function deletePurchase(id) {
  const matches = await db.filter(COLLECTIONS.PURCHASES.name, (p) => String(p.id) === String(id));
  const vendorId = matches?.[0]?.vendor?.id;
  const result = await PurchaseService.deletePurchase(id);
  const currentShiftId = ShiftHelper.getCurrentShiftId()?.id;
  if (currentShiftId) {
    const existing = await ShiftHelper.filterShiftPayments({ shift_id: Number(currentShiftId) });
    const txns = existing.filter(
      (sp) => sp.type === CASHBOOK_ENTRY_TYPE.PURCHASE && String(sp.purchase_id) === String(id),
    );
    for (const t of txns) await db.delete(COLLECTIONS.SHIFT_TRANSACTIONS.name, t.id);
  }
  if (matches?.[0]?.local_id) {
    await db.delete(COLLECTIONS.PURCHASES.name, Number(matches[0].local_id));
  }
  if (vendorId && Number(vendorId) > 0) {
    VendorHelper.fetchAndUpdateVendorFromBE(vendorId);
  }
  return result;
}

export async function saveDraftPurchase(data) {
  const { invoiceNo, vendor, purchaseDate, billDate, paymentDate, items, paidAmount, paymentModes = [] } = data;

  const currentShiftIdObj = ShiftHelper.getCurrentShiftId() || null;
  const totalPurchaseAmount = (items || []).reduce((sum, item) => sum + Number(item.total || item.payable || 0), 0);

  const payload = {
    date: DateTimeHelpers.getLocalISOString(),
    invoiceNo,
    vendor,
    purchaseDate,
    billDate,
    paymentDate,
    items,
    paidAmount: Number(paidAmount),
    paymentModes: (paymentModes || []).map((pm) => ({
      mode_id: Number(pm.mode_id || pm.id),
      name: pm.name,
      amount: Number(pm.amount) || 0,
    })),
    id: null,
    status: DRAFT_STATUS.DRAFT,
    shift: currentShiftIdObj,
    totalPurchaseAmount,
  };

  return await db.add(COLLECTIONS.PURCHASES.name, payload);
}

export async function updateDraftPurchase(localId, data) {
  const {
    invoiceNo,
    vendor,
    purchaseDate,
    billDate,
    paymentDate,
    items,
    paidAmount,
    paymentModes = [],
    id = null,
  } = data;

  const currentShiftObj = ShiftHelper.getCurrentShiftId() || null;
  const totalPurchaseAmount = (items || []).reduce((sum, item) => sum + Number(item.total || item.payable || 0), 0);

  const payload = {
    date: DateTimeHelpers.getLocalISOString(),
    invoiceNo,
    vendor,
    purchaseDate,
    billDate,
    paymentDate,
    items,
    paidAmount: Number(paidAmount),
    paymentModes: (paymentModes || []).map((pm) => ({
      mode_id: Number(pm.mode_id || pm.id),
      name: pm.name,
      amount: Number(pm.amount) || 0,
    })),
    id: id,
    status: id ? DRAFT_STATUS.SAVED : DRAFT_STATUS.DRAFT,
    shift: currentShiftObj,
    totalPurchaseAmount,
  };

  return await db.update(COLLECTIONS.PURCHASES.name, Number(localId), payload);
}

export async function deleteDraftPurchase(localId) {
  return await db.delete(COLLECTIONS.PURCHASES.name, Number(localId));
}

const parseValidId = (val) => {
  if (val == null || val === "") return null;
  const num = Number(val);
  return !isNaN(num) ? num : null;
};

export async function saveOrUpdateSavedPurchase(purchaseId, data) {
  const matches = purchaseId
    ? await db.filter(COLLECTIONS.PURCHASES.name, (p) => String(p.id) === String(purchaseId))
    : [];
  const match = matches?.[0];

  const currentShiftId = ShiftHelper.getCurrentShiftId()?.id || null;
  const validId = parseValidId(purchaseId) ?? parseValidId(data?.id);
  const totalPurchaseAmount = (data.items || []).reduce(
    (sum, item) => sum + Number(item.total || item.payable || 0),
    0,
  );

  const payload = {
    date: data.purchaseDate
      ? DateTimeHelpers.getLocalISOString(new Date(data.purchaseDate))
      : DateTimeHelpers.getLocalISOString(),
    invoiceNo: data.invoiceNo,
    vendor: data.vendor,
    purchaseDate: data.purchaseDate,
    billDate: data.billDate,
    paymentDate: data.paymentDate,
    items: data.items,
    paidAmount: Number(data.paidAmount || 0),
    paymentModes: (data.paymentModes || []).map((pm) => ({
      mode_id: Number(pm.mode_id || pm.id),
      name: pm.name,
      amount: Number(pm.amount) || 0,
    })),
    id: validId,
    status: DRAFT_STATUS.SAVED,
    shift: currentShiftId ? { id: currentShiftId } : null,
    totalPurchaseAmount,
  };

  if (match && match.local_id) {
    return await db.update(COLLECTIONS.PURCHASES.name, Number(match.local_id), {
      ...match,
      ...payload,
    });
  } else {
    return await db.add(COLLECTIONS.PURCHASES.name, payload);
  }
}

function applyPurchasePaymentModes(data, fetchedPaidAmount, shopPaymentModes, setPaymentModes) {
  const fetchedModes = data.paymentModes || data.payment_modes || [];
  if (Array.isArray(fetchedModes) && fetchedModes.length > 0) {
    const existingMap = new Map(fetchedModes.map((pm) => [pm.mode_id || pm.id, Number(pm.amount) || 0]));
    if (Array.isArray(shopPaymentModes) && shopPaymentModes.length > 0) {
      setPaymentModes(
        shopPaymentModes.map((spm) => ({
          mode_id: spm.id,
          name: spm.name,
          amount: existingMap.has(spm.id) ? existingMap.get(spm.id) : 0,
        })),
      );
    } else {
      setPaymentModes(
        fetchedModes.map((pm) => ({
          mode_id: pm.mode_id || pm.id,
          name: pm.name,
          amount: Number(pm.amount) || 0,
        })),
      );
    }
  } else if (fetchedPaidAmount > 0 && Array.isArray(shopPaymentModes) && shopPaymentModes.length > 0) {
    setPaymentModes(
      shopPaymentModes.map((spm, idx) => ({
        mode_id: spm.id,
        name: spm.name,
        amount: idx === 0 ? fetchedPaidAmount : 0,
      })),
    );
  }
}

function applyPurchaseDates(data, isDraftMode, setPurchaseDate, setBillDate, setPaymentDate) {
  if (data.purchaseDate || data.date) {
    const dateToParse = data.purchaseDate || data.date;
    const d = isDraftMode ? new Date(dateToParse) : DateTimeHelpers.parseLocalISO(dateToParse);
    if (!isNaN(d.getTime())) {
      setPurchaseDate(DateTimeHelpers.getLocalISODate(d));
    }
  }
  if (data.billDate) {
    setBillDate(data.billDate);
  } else if (data.bill_date) {
    setBillDate(DateTimeHelpers.formatDMYToISODate(data.bill_date));
  }
  if (data.paymentDate) {
    setPaymentDate(data.paymentDate);
  } else if (data.payment_date) {
    setPaymentDate(DateTimeHelpers.formatDMYToISODate(data.payment_date));
  }
}

export function mapServerPurchaseItems(purchaseItems = [], serverItems) {
  if (Array.isArray(serverItems) && serverItems.length > 0) {
    return serverItems.map((serverItem) => {
      const matchedLocalItem = (purchaseItems || []).find((purchaseItem) => purchaseItem.plu === serverItem.plu);

      return calculatePurchaseItem({
        ...matchedLocalItem,
        id: serverItem.id,
        isExisting: true,
      });
    });
  }
  return (purchaseItems || []).map((item) => ({ ...item, isExisting: true }));
}

export function applyPurchaseDetailsData(data, isDraftMode, vendors, shopPaymentModes, setters) {
  const {
    setInvoiceNo,
    setVendor,
    setPaidAmount,
    setPaymentModes,
    setPurchaseDate,
    setBillDate,
    setPaymentDate,
    setPurchaseItems,
  } = setters;

  setInvoiceNo(data.invoiceNo || data.invoice_no || "");
  const vendorId = data.vendor?.id;
  setVendor(vendors.find((v) => v.id === vendorId) || data.vendor || null);
  const fetchedPaidAmount = data.paidAmount || data.paid_amount || 0;
  setPaidAmount(fetchedPaidAmount);

  applyPurchasePaymentModes(data, fetchedPaidAmount, shopPaymentModes, setPaymentModes);
  applyPurchaseDates(data, isDraftMode, setPurchaseDate, setBillDate, setPaymentDate);

  if (isDraftMode) {
    setPurchaseItems(data.items);
  } else {
    setPurchaseItems(mapServerPurchaseItems(data.items));
  }
}
